


import {
  ref,
  defineComponent,
  computed,
  onMounted,
  reactive,
  onUnmounted,
  watch,
} from '@vue/composition-api';
import { useStore } from '@/hooks/useStore';
import { useRoute } from '@/hooks/useRoute';
import ExtremeMap from '@/components/lib/ExtremeMap/index.vue';
import PrintPagination from '@/components/lib/PrintPagination/index.vue';
import {
  convReport,
  convSearchParamTsTo,
  loadEmergencyResponseTeamMtxs,
} from '@/components/ExternalReports/EmergencyResponseTeam/utils/index';
import { Settings as UserSettings } from '@/models/apis/user/userResponse';
import { ExtremeMapEssentials } from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamMap';
import emergencyResponseTeamReportApi from '@/apis/externalReports/emergencyResponseTeam/emergency_response_team_report';
import { waitForUserAndMasters } from '@/lib/masterHelper';
import { dtFormat, dateStrToDate } from '@/lib/dateHelper';
import { setPrintPageSize } from '@/lib/printPageHelper';
import {
  PHOTO_HEADER_DIVIDING_MARK,
  PHOTO_NUM_LIMIT_PER_PAGE,
  CURRENT_PAGE_DEFAULT,
  ITEMS_PER_PAGE_DEFAULT,
  ITEMS_PER_PAGE_LIMIT,
} from '@/consts/externalReports/emergencyResponseTeam/report_print';
import type {
  EmergencyResponseTeamReportWithPhotos,
  EmergencyResponseTeamReportExt,
  EmergencyResponseTeamReportPhotoExt,
} from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamReport';
import { ExternalReportSettings, ExternalReportsLabelPc } from '@/models/apis/externalReports/external_report_settings';
import { useRequestGuard } from '@/composables/useRequestGuard';
import { requestPhotos, revokePhotosObjectURLs } from '@/lib/imageHelper';
import { pairPrintReportsWithMaps } from '@/lib/externalReports/externalReportHelper';
import { EXTERNAL_REPORT_TYPE } from '@/consts/externalReports/emergencyResponseTeam';

interface EmergencyResponseTeamPrintReport extends EmergencyResponseTeamReportExt {
  reportPhotos: Record<string, EmergencyResponseTeamReportPhotoExt[]>;
  isMapAvailable: boolean;
}
interface PrintEmergencyResponseTeamReportState {
  reports: EmergencyResponseTeamReportExt[];
  paginatedReports: EmergencyResponseTeamPrintReport[];
  extremeMapEssentials: ExtremeMapEssentials | null;
  inputItemsPerPage: string;
}
export default defineComponent({
  setup() {
    const state = reactive<PrintEmergencyResponseTeamReportState>({
      reports: [],
      paginatedReports: [],
      extremeMapEssentials: null,
      inputItemsPerPage: ITEMS_PER_PAGE_DEFAULT.toString(),
    });
    const shouldShowPagination = computed<boolean>(() => {
      return state.reports.length > itemsPerPage.value;
    });
    const store = useStore();
    const userState = store.state.user;
    const { doRequest, isRequesting } = useRequestGuard((errorMessage) => {
      console.error(errorMessage);
    });
    const userSettings = computed<UserSettings>(() => {
      return userState.settings;
    });
    const externalReportUserSettings = ref<ExternalReportSettings | null>(null);
    const pcLabels = computed<ExternalReportsLabelPc>(() => {
      return externalReportUserSettings.value?.pc ?? {
        title: {},
        button: {},
        field_label: {},
        table_header: {},
      } as ExternalReportsLabelPc;
    });
    const { route, router } = useRoute();
    const externalReportId = computed<string>(() => {
      return route.value.params.externalReportId;
    });
    const currentPage = computed<number>(() => {
      const raw = Number(route.value.params.currentPage);
      const isNumeric = Number.isFinite(raw);
      const n = isNumeric ? Math.trunc(raw) : CURRENT_PAGE_DEFAULT;
      return n >= CURRENT_PAGE_DEFAULT ? n : CURRENT_PAGE_DEFAULT;
    });
    const itemsPerPage = computed<number>(() => {
      const raw = Number(route.value.params.itemsPerPage);
      const n = Number.isFinite(raw) ? Math.trunc(raw) : NaN;
      if (!Number.isFinite(n) || n < CURRENT_PAGE_DEFAULT) return ITEMS_PER_PAGE_DEFAULT;
      // 1ページあたりの表示件数が上限を超えている場合は上限値を設定
      return Math.min(n, ITEMS_PER_PAGE_LIMIT);
    });
    const reportDtFormat = (dt: Date) => {
      return dtFormat(dt, 'yyyy年m月d日 HH時MM分');
    };
    const extremeMap = ref<InstanceType<typeof ExtremeMap>[]>([]);
    const convPrintReport = async(reports: EmergencyResponseTeamReportExt[]): Promise<EmergencyResponseTeamPrintReport[]> => {
      await loadEmergencyResponseTeamMtxs(reports);
      const resultPromises = reports.map(async(report) => {
        await requestPhotos(report.photosExt);

        return {
          ...report,
          reportPhotos: convReportPhotos(report.photosExt),
          isMapAvailable: false,
        };
      });
      return Promise.all(resultPromises);
    };
    const convReportPhotos = (photos: EmergencyResponseTeamReportPhotoExt[]): Record<string, EmergencyResponseTeamReportPhotoExt[]> => {
      const reportPhotos: Record<string, EmergencyResponseTeamReportPhotoExt[]> = {};
      if (!externalReportUserSettings.value) return reportPhotos;

      externalReportUserSettings.value.choices.photo_types.forEach(photoType => {
        const reportTypePhotos = photos.filter(photo => photo.photoType === photoType);
        if (reportTypePhotos.length === 0) return;

        for (let i = 0; i < Math.ceil(reportTypePhotos.length / PHOTO_NUM_LIMIT_PER_PAGE); i++) {
          const start = i * PHOTO_NUM_LIMIT_PER_PAGE;
          const end = (i + 1) * PHOTO_NUM_LIMIT_PER_PAGE;
          const key = i + PHOTO_HEADER_DIVIDING_MARK + photoType;
          reportPhotos[key] = reportTypePhotos.slice(start, end);
        }
      });
      return reportPhotos;
    };
    const getPhotoType = (photoHeaderInfo: string): string => {
      return photoHeaderInfo.split(PHOTO_HEADER_DIVIDING_MARK)[1];
    };
    const searchReport = async(): Promise<EmergencyResponseTeamReportWithPhotos[]> => {
      if (externalReportId.value) {
        const report = await emergencyResponseTeamReportApi.get(Number(externalReportId.value));
        return [report];
      }
      const hanName = route.value.params.hanName;
      const searchParam = {
        ts_from: dateStrToDate(route.value.params.dateFrom) || new Date(),
        ts_to: convSearchParamTsTo(dateStrToDate(route.value.params.dateTo) || new Date()),
        han_name: hanName === 'all' ? '' : hanName,
      };
      const reports = await emergencyResponseTeamReportApi.index(searchParam);
      return reports;
    };
    watch(() => extremeMap.value, () => {
      if (!extremeMap.value) return;

      showEmergencyResponseTeamReport();
    });
    const showEmergencyResponseTeamReport = () => {
      const h = Math.max(240, window.innerHeight - 300);
      if (!externalReportUserSettings.value) return;

      for (const { report, map } of pairPrintReportsWithMaps(state.paginatedReports, extremeMap.value)) {
        // extremeMap.valueでwatchすると、地図を表示し終わったものも含まれるため、
        // 地図を表示し終わったものは処理せず再描画されるのを防ぐ
        if (report.isMapAvailable) {
          continue;
        }
        map.setMapHeight(h);
        map.triggerResize();
        map.removeExternalReportLayer();
        map.showExternalReportLayer(report, {
          photoTypeVisibility: externalReportUserSettings.value.choices.photo_types.reduce((acc, type) => {
            acc[type] = true;
            return acc;
          }, {} as Record<string, boolean>),
          fitToExtent: true,
          externalReportUserSettings: externalReportUserSettings.value,
          showTrack: true,
        });
        report.isMapAvailable = true;
      }
    };
    const paginatePrintReport = async() => {
      const nextPageIdx = currentPage.value * itemsPerPage.value;
      const minIdx = nextPageIdx - itemsPerPage.value;
      const reports = state.reports.slice(minIdx, nextPageIdx);
      await doRequest(async() => {
        state.paginatedReports = await convPrintReport(reports);
      }, '報告書の取得に失敗しました。');
    };
    const moveToSelectedPage = (currentPage: number, itemsPerPage: number) => {
      // ページ切り替え時に地図が表示されなくなる問題を回避するため、リダイレクトしてページングする
      const routeObj = {
        name: 'PrintExternalReportsEmergencyResponseTeamReportAll',
        params: {
          dateFrom: route.value.params.dateFrom,
          dateTo: route.value.params.dateTo,
          hanName: route.value.params.hanName,
          currentPage: currentPage.toString(),
          itemsPerPage: itemsPerPage.toString(),
        },
      };
      const obj = router.resolve(routeObj);
      window.location.href = obj.href;
    };
    const onClickPageChange = (pageNum: number) => {
      moveToSelectedPage(pageNum, itemsPerPage.value);
    };
    const onClickItemsPerPageChange = (itemsPerPage: number) => {
      moveToSelectedPage(CURRENT_PAGE_DEFAULT, itemsPerPage);
    };
    onMounted(async() => {
      document.body.classList.add('A4');
      setPrintPageSize('A4 portrait');

      await doRequest(async() => {
        await waitForUserAndMasters();
        const reports = await searchReport();
        state.reports = reports.map(e =>
          convReport(e),
        );
      }, '初期化に失敗しました。');
      externalReportUserSettings.value = window.master.externalReportSettings?.find(
        setting => setting.external_report_type === EXTERNAL_REPORT_TYPE,
      ) ?? null;

      state.extremeMapEssentials = {
        userSettings: userSettings.value,
        kpMap: window.master.kpMap,
        externalReportSettings: externalReportUserSettings.value,
      };
      state.inputItemsPerPage = itemsPerPage.value.toString();
      await paginatePrintReport();
    });
    onUnmounted(() => {
      document.body.classList.remove('A4');
      state.reports.map(report => {
        if (!report.photosExt) return;

        revokePhotosObjectURLs(report.photosExt.map(photo => photo.savedImage));
      });
    });

    return {
      state,
      // ref
      extremeMap,
      // computed
      shouldShowPagination,
      currentPage,
      itemsPerPage,
      isRequesting,
      pcLabels,
      reportDtFormat,
      dtFormat,
      getPhotoType,
      onClickPageChange,
      onClickItemsPerPageChange,
    };
  },
  components: {
    PrintPagination,
  },
});
